import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Modal, Button, Form, Row, Col, Spinner } from 'react-bootstrap';
import { FaFilter, FaTimes } from 'react-icons/fa';

const TarifariosEmpresasFilters = ({
    show,
    onHide,
    onFiltersChange,
    onClearFilters,
    initialFilters = {},
    servicios = [],
    empresas = [],
    regiones = [],
    comunas = [],
    loadingEmpresas = false,
    onRegionChange
}) => {
    // Estados para filtros
    const [filtros, setFiltros] = useState({
        servicioId: initialFilters.servicioId || '',
        empresaId: initialFilters.empresaId || '',
        regionId: initialFilters.regionId || '',
        comunaId: initialFilters.comunaId || ''
    });

    // Actualizar filtros cuando cambien los initialFilters
    useEffect(() => {
        setFiltros({
            servicioId: initialFilters.servicioId || '',
            empresaId: initialFilters.empresaId || '',
            regionId: initialFilters.regionId || '',
            comunaId: initialFilters.comunaId || ''
        });
    }, [initialFilters]);

    // Handlers para cambios en filtros
    const handleServicioChange = (value) => {
        setFiltros(prev => ({
            ...prev,
            servicioId: value
        }));
    };

    const handleEmpresaChange = (value) => {
        setFiltros(prev => ({
            ...prev,
            empresaId: value
        }));
    };

    const handleRegionChange = (value) => {
        setFiltros(prev => ({
            ...prev,
            regionId: value,
            comunaId: '' // Limpiar comuna cuando cambia región
        }));
        // Notificar cambio para actualizar comunas disponibles
        if (onRegionChange) {
            onRegionChange(value);
        }
    };

    const handleComunaChange = (value) => {
        setFiltros(prev => ({
            ...prev,
            comunaId: value
        }));
    };

    // Función para aplicar filtros
    const handleAplicarFiltros = () => {
        onFiltersChange(filtros);
        onHide();
    };

    // Función para limpiar filtros
    const limpiarFiltros = () => {
        const filtrosVacios = {
            servicioId: '',
            empresaId: '',
            regionId: '',
            comunaId: ''
        };
        setFiltros(filtrosVacios);
        if (onClearFilters) {
            onClearFilters();
        } else {
            onFiltersChange(filtrosVacios);
        }
        onHide();
    };

    // Opciones para dropdowns
    const renderServicioOptions = () => [
        <option key="" value="">Todos los servicios</option>,
        ...servicios.map(servicio => (
            <option key={servicio.id} value={servicio.id}>
                {servicio.servicio}
            </option>
        ))
    ];

    const renderEmpresaOptions = () => [
        <option key="" value="">Todas las empresas</option>,
        ...empresas.map(empresa => (
            <option key={empresa.id} value={empresa.id}>
                {empresa.sigla}
            </option>
        ))
    ];

    const renderRegionOptions = () => [
        <option key="" value="">Todas las regiones</option>,
        ...regiones.map(region => (
            <option key={region.id} value={region.id}>
                {region.region}
            </option>
        ))
    ];

    const renderComunaOptions = () => [
        <option key="" value="">Todas las comunas</option>,
        ...comunas.map(comuna => (
            <option key={comuna.id} value={comuna.id}>
                {comuna.comuna}
            </option>
        ))
    ];

    return (
        <Modal show={show} onHide={onHide} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>
                    <FaFilter className="me-2" />
                    Filtros de Tarifarios de Empresas
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Row>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Servicio Spread</Form.Label>
                            <Form.Select
                                value={filtros.servicioId}
                                onChange={(e) => handleServicioChange(e.target.value)}
                            >
                                {renderServicioOptions()}
                            </Form.Select>
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>
                                Empresa
                                {loadingEmpresas && (
                                    <Spinner 
                                        animation="border" 
                                        size="sm" 
                                        className="ms-2"
                                    />
                                )}
                            </Form.Label>
                            <Form.Select
                                value={filtros.empresaId}
                                onChange={(e) => handleEmpresaChange(e.target.value)}
                                disabled={loadingEmpresas}
                            >
                                {renderEmpresaOptions()}
                            </Form.Select>
                        </Form.Group>
                    </Col>
                </Row>

                <Row>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Región</Form.Label>
                            <Form.Select
                                value={filtros.regionId}
                                onChange={(e) => handleRegionChange(e.target.value)}
                            >
                                {renderRegionOptions()}
                            </Form.Select>
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Comuna</Form.Label>
                            <Form.Select
                                value={filtros.comunaId}
                                onChange={(e) => handleComunaChange(e.target.value)}
                            >
                                {renderComunaOptions()}
                            </Form.Select>
                        </Form.Group>
                    </Col>
                </Row>

                {/* Información adicional */}
                <div className="bg-light p-3 rounded">
                    <small className="text-muted">
                        <strong>Tip:</strong> Deje los campos vacíos para mostrar todos los resultados de esa categoría.
                        Las comunas se actualizan automáticamente según la región seleccionada.
                    </small>
                </div>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="outline-secondary" onClick={limpiarFiltros}>
                    <FaTimes className="me-1" />
                    Limpiar Filtros
                </Button>
                <Button variant="secondary" onClick={onHide}>
                    Cancelar
                </Button>
                <Button variant="primary" onClick={handleAplicarFiltros}>
                    <FaFilter className="me-1" />
                    Aplicar Filtros
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

TarifariosEmpresasFilters.propTypes = {
    show: PropTypes.bool.isRequired,
    onHide: PropTypes.func.isRequired,
    onFiltersChange: PropTypes.func.isRequired,
    onClearFilters: PropTypes.func,
    initialFilters: PropTypes.object,
    servicios: PropTypes.array,
    empresas: PropTypes.array,
    regiones: PropTypes.array,
    comunas: PropTypes.array,
    loadingEmpresas: PropTypes.bool,
    onRegionChange: PropTypes.func
};

TarifariosEmpresasFilters.defaultProps = {
    initialFilters: {},
    servicios: [],
    empresas: [],
    regiones: [],
    comunas: [],
    loadingEmpresas: false,
    onRegionChange: null,
    onClearFilters: null
};

export default TarifariosEmpresasFilters;
